import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useSubscription } from './SubscriptionContext';
import { getApiBaseUrl } from '../utils/backendBaseUrl';
import { useWorkspace } from '../workspace/state/workspaceStore';

type UserActiveContext =
  | { scope: 'none' }
  | { scope: 'student'; studentId: string }
  | { scope: 'class'; classId: string };

type UsageCounters = {
  studentsCount?: number;
  classesCount?: number;
};

type BackendUserContextResponse = {
  roleType?: string;
  activeMode?: 'student' | 'class';
  activeContext?: any;
  usageCounters?: UsageCounters;
};

type UserContextValue = {
  loading: boolean;
  hasLoadedOnce: boolean;
  error: string | null;

  roleType: string;
  plan: string;
  planName: string;
  activeMode: 'student' | 'class';
  activeContext: UserActiveContext;
  usageCounters: UsageCounters;

  refresh: () => Promise<void>;
};

const API = getApiBaseUrl();

const UserContext = createContext<UserContextValue | null>(null);

export function useUserContext() {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUserContext must be used within UserContextProvider');
  return ctx;
}

function isReadyScope(scope: UserActiveContext): boolean {
  if (!scope) return false;
  if (scope.scope === 'student') return Boolean((scope as any).studentId);
  if (scope.scope === 'class') return Boolean((scope as any).classId);
  return false;
}

function safeNormalizeScope(raw: any): UserActiveContext {
  const scope = String(raw?.scope || '').trim().toLowerCase();
  if (scope === 'student') {
    const studentId = String(raw?.studentId || '').trim();
    return studentId ? { scope: 'student', studentId } : { scope: 'none' };
  }
  if (scope === 'class') {
    const classId = String(raw?.classId || '').trim();
    return classId ? { scope: 'class', classId } : { scope: 'none' };
  }
  return { scope: 'none' };
}

export function UserContextProvider(props: { children: React.ReactNode }) {
  const { currentUser, userProfile, emailVerified } = useAuth() as any;
  const { subscription } = useSubscription() as any;
  const ws = useWorkspace();

  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usageCounters, setUsageCounters] = useState<UsageCounters>({});
  const lastSavedJsonRef = useRef<string>('');
  const saveTimerRef = useRef<any>(null);
  const hasLoadedOnceRef = useRef(false);

  // Reset state on logout / email-not-verified
  useEffect(() => {
    if (!currentUser || emailVerified === false) {
      setHasLoadedOnce(false);
      setUsageCounters({});
      setError(null);
      setLoading(false);
      hasLoadedOnceRef.current = false;
      lastSavedJsonRef.current = '';
    }
  }, [currentUser, emailVerified]);

  const roleType = String(userProfile?.ruolo || userProfile?.role || userProfile?.roleType || 'docente');
  const plan = String(subscription?.plan || 'free');
  const planName = String(subscription?.plan_name || subscription?.planName || 'Free');
  const activeMode = ws.mode;
  const activeContext = ws.getScope() as UserActiveContext;

  const fetchContext = useCallback(async () => {
    if (!currentUser) return;
    // Se email non verificata, backend non accessibile.
    if (emailVerified === false) return;

    setLoading(true);
    setError(null);

    try {
      const token = await currentUser.getIdToken();
      const res = await axios.get<BackendUserContextResponse>(`${API}/users/context`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000,
      });

      const serverActiveMode = (res.data?.activeMode === 'class' ? 'class' : 'student') as 'student' | 'class';
      const serverScope = safeNormalizeScope(res.data?.activeContext);
      const serverCounters = (res.data?.usageCounters || {}) as UsageCounters;

      setUsageCounters(serverCounters);

      // Se l'utente non ha ancora scelto nulla localmente, ripristina dal server.
      const localScope = ws.getScope() as UserActiveContext;
      if (!isReadyScope(localScope) && isReadyScope(serverScope)) {
        if (serverScope.scope === 'student') ws.setActiveStudent(serverScope.studentId);
        if (serverScope.scope === 'class') ws.setActiveClass(serverScope.classId);
      } else if (!isReadyScope(localScope) && !isReadyScope(serverScope)) {
        // Solo in assenza di selezione, allinea la modalità.
        if (serverActiveMode === 'class') ws.setModeClass();
        else ws.setModeStudent();
      }

      // Evita di riscrivere subito lo stesso valore appena caricato.
      const syncedPayload = JSON.stringify({
        activeMode: serverActiveMode,
        activeContext: serverScope,
      });
      lastSavedJsonRef.current = syncedPayload;
      hasLoadedOnceRef.current = true;
      setHasLoadedOnce(true);
    } catch (e: any) {
      setError('Impossibile caricare il contesto utente.');
      // Anche in errore consideriamo “tentato” per evitare blocchi permanenti.
      setHasLoadedOnce(true);
    } finally {
      setLoading(false);
    }
  }, [currentUser, emailVerified, ws]);

  const saveContext = useCallback(
    async (payload: { activeMode: 'student' | 'class'; activeContext: UserActiveContext }) => {
      if (!currentUser) return;
      if (emailVerified === false) return;

      try {
        const token = await currentUser.getIdToken();
        await axios.put(
          `${API}/users/context`,
          {
            activeMode: payload.activeMode,
            activeContext: payload.activeContext,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 8000,
          }
        );
      } catch {
        // non bloccare UI
      }
    },
    [currentUser, emailVerified]
  );

  // Load from backend on auth-ready
  useEffect(() => {
    void fetchContext();
  }, [fetchContext]);

  // Persist to backend when selection changes (debounced)
  useEffect(() => {
    if (!currentUser) return;
    if (emailVerified === false) return;

    // aspetta il primo load per evitare clobber tra storage locale e server
    if (!hasLoadedOnceRef.current) return;

    const payload = {
      activeMode: ws.mode,
      activeContext: ws.getScope() as UserActiveContext,
    };

    const json = JSON.stringify(payload);
    if (json === lastSavedJsonRef.current) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      lastSavedJsonRef.current = json;
      void saveContext(payload);
    }, 600);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [currentUser, emailVerified, ws.mode, ws.activeStudentId, ws.activeClassId, ws, saveContext]);

  const refresh = useCallback(async () => {
    await fetchContext();
  }, [fetchContext]);

  const value = useMemo<UserContextValue>(
    () => ({
      loading,
      hasLoadedOnce,
      error,
      roleType,
      plan,
      planName,
      activeMode,
      activeContext,
      usageCounters,
      refresh,
    }),
    [loading, hasLoadedOnce, error, roleType, plan, planName, activeMode, activeContext, usageCounters, refresh]
  );

  return <UserContext.Provider value={value}>{props.children}</UserContext.Provider>;
}
